import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FavoritesContentService } from '@modules/favorites/services/favorites-content.service';
import { FavoritesService } from '@modules/favorites/services/favorites.service';
import { DownloadService } from '@modules/shared/services/download.service';
import { ShareModalService } from '@modules/shared/services/share.service';
import { TrackingService, TRACKING_TYPES } from '@modules/shared/services/tracking.service';

import { Action, ActionResourceInterface, ActionType } from './action.types';

type ButtonConfiguration = { action: Action; icon: string; label: string };

@Component({
  selector: 'mb-action-button-group',
  templateUrl: './action-button-group.component.html',
  styleUrls: ['./action-button-group.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActionButtonGroupComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private favoritesContentService = inject(FavoritesContentService);
  private favoriteService = inject(FavoritesService);
  private shareService = inject(ShareModalService);
  private downloadService = inject(DownloadService);
  private trackingService = inject(TrackingService);
  elRef = inject(ElementRef);

  @Input()
  actions: Action[] = [{ type: 'SHARE' }, { type: 'DOWNLOAD' }, { type: 'BOOKMARK' }];

  @Input()
  darkTheme: boolean = true;

  @Input()
  resource?: ActionResourceInterface;

  public buttons: ButtonConfiguration[] = [];

  private destroy$ = new Subject<boolean>();
  private buttonConfiguration: { [k in ActionType]: ButtonConfiguration } = {
    SHARE: {
      action: { type: 'SHARE' },
      icon: 'share',
      label: 'SHARE',
    },
    DOWNLOAD: {
      action: { type: 'DOWNLOAD' },
      icon: 'download',
      label: 'DOWNLOAD',
    },
    BOOKMARK: {
      action: { type: 'BOOKMARK' },
      icon: 'bds/bookmark/16',
      label: 'BOOKMARK',
    },
  };

  constructor() {
    // FIXME: code smell
    this.favoritesContentService.favorites$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.resource) {
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.actions.forEach((action) => {
      const button = this.buttonConfiguration[action.type];
      button.action = action;
      this.buttons?.push(button);
    });
  }

  // TODO: tracking these events should be done in the respective services (future refactorization)
  actionClick($event: Event, button: ButtonConfiguration) {
    $event.stopPropagation();
    $event.preventDefault();
    if (button.action.onClick) {
      // used for pre-media download action
      button.action.onClick($event);
    } else {
      if (this.resource) {
        if (button.action.type === 'BOOKMARK') {
          if (this.resource.favoriteData) {
            if (!this.favoriteService.isFavorite(this.resource.favoriteData.id)) {
              this.trackingService.trackEvent(this.resource, TRACKING_TYPES.BOOKMARK);
            }
            this.favoriteService.toggle(this.resource);
          }
        } else if (button.action.type === 'SHARE') {
          if (this.resource.shareData) {
            this.shareService.openModal(this.resource);
            this.trackingService.trackEvent(this.resource, TRACKING_TYPES.SHARE);
          }
        } else if (button.action.type === 'DOWNLOAD') {
          if (this.resource) {
            const downloadCallback = () => {
              // track download event after the user actually clicked download
              this.trackingService.trackEvent(this.resource, TRACKING_TYPES.DOWNLOAD);
            };

            this.downloadService.download({
              data: this.resource,
              isShareDownload: false,
              pushToDataLayer: true,
              downloadCb: downloadCallback,
            });
          }
        }
      }
    }
  }

  get isBookmarked() {
    if (!this.resource?.favoriteData) {
      return false;
    }
    return this.favoriteService.isFavorite(this.resource.favoriteData.id);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
